<script setup>
import { Icon } from '@iconify/vue';
import { Link } from '@inertiajs/inertia-vue3';

defineProps({
    actives: {
        type: Number,
        default: 0,
    },
    news: {
        type: Number,
        default: 0,
    },
    olds: {
        type: Number,
        default: 0,
    },
    inactives: {
        type: Number,
        default: 0,
    },
    total: {
        type: Number,
        default: 0,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    error: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['reload']);
</script>

<template>
    <div class="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200/80 dark:border-slate-800 p-5 shadow-sm">
        <!-- Cabecera del Grid de Métricas -->
        <div class="flex items-center justify-between gap-3 mb-4 pb-3 border-b border-gray-100 dark:border-slate-800">
            <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shadow-xs">
                    <Icon icon="tabler:users-group" class="w-5 h-5" />
                </div>
                <div>
                    <h3 class="text-base font-bold text-gray-900 dark:text-white leading-tight">
                        Métricas de Estudiantes
                    </h3>
                    <p class="text-xs text-gray-500 dark:text-gray-400">Resumen general de matrícula y estado</p>
                </div>
            </div>

            <div class="flex items-center gap-2">
                <button
                    type="button"
                    @click="emit('reload')"
                    :disabled="loading"
                    title="Actualizar métricas"
                    class="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition"
                >
                    <Icon icon="tabler:refresh" class="w-4 h-4" :class="{ 'animate-spin text-emerald-600': loading }" />
                </button>
                <Link
                    :href="route('student-list')"
                    class="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 rounded-xl text-xs font-semibold transition"
                >
                    <span>Ver Alumnos</span>
                    <Icon icon="tabler:arrow-right" class="w-3.5 h-3.5" />
                </Link>
            </div>
        </div>

        <!-- Estado de Error / Lentitud -->
        <div v-if="error" class="p-4 bg-rose-50 dark:bg-rose-950/30 rounded-xl border border-rose-200 dark:border-rose-900/40 text-center flex items-center justify-between">
            <p class="text-xs text-rose-700 dark:text-rose-300">{{ error }}</p>
            <button
                type="button"
                @click="emit('reload')"
                class="px-2.5 py-1 text-xs bg-rose-600 text-white font-semibold rounded-lg shadow-xs hover:bg-rose-700"
            >
                Reintentar
            </button>
        </div>

        <!-- Estado de Carga (Skeleton) -->
        <div v-else-if="loading" class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 animate-pulse">
            <div v-for="i in 5" :key="i" class="h-24 bg-gray-100 dark:bg-slate-800/80 rounded-2xl"></div>
        </div>

        <!-- Grid de Tarjetas KPI -->
        <div v-else class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <!-- 1. Alumnos Activos -->
            <Link
                :href="route('student-list', { status: '1' })"
                class="p-4 rounded-2xl bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent border border-emerald-200/60 dark:border-emerald-800/60 hover:border-emerald-500 transition group hover:shadow-sm"
            >
                <div class="flex items-center justify-between mb-2">
                    <span class="text-xs font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">Activos</span>
                    <Icon icon="tabler:user-check" class="w-4 h-4 text-emerald-600 group-hover:scale-110 transition-transform" />
                </div>
                <div class="text-2xl font-black text-gray-900 dark:text-white">
                    {{ actives }}
                </div>
                <p class="text-[11px] text-gray-400 mt-0.5">En clases vigentes</p>
            </Link>

            <!-- 2. Alumnos Nuevos -->
            <Link
                :href="route('student-list')"
                class="p-4 rounded-2xl bg-gradient-to-br from-blue-500/10 via-blue-500/5 to-transparent border border-blue-200/60 dark:border-blue-800/60 hover:border-blue-500 transition group hover:shadow-sm"
            >
                <div class="flex items-center justify-between mb-2">
                    <span class="text-xs font-bold text-blue-700 dark:text-blue-400 uppercase tracking-wider">Nuevos</span>
                    <Icon icon="tabler:user-plus" class="w-4 h-4 text-blue-600 group-hover:scale-110 transition-transform" />
                </div>
                <div class="text-2xl font-black text-gray-900 dark:text-white">
                    {{ news }}
                </div>
                <p class="text-[11px] text-gray-400 mt-0.5">Ingresantes este año</p>
            </Link>

            <!-- 3. Alumnos Antiguos -->
            <Link
                :href="route('student-list')"
                class="p-4 rounded-2xl bg-gradient-to-br from-indigo-500/10 via-indigo-500/5 to-transparent border border-indigo-200/60 dark:border-indigo-800/60 hover:border-indigo-500 transition group hover:shadow-sm"
            >
                <div class="flex items-center justify-between mb-2">
                    <span class="text-xs font-bold text-indigo-700 dark:text-indigo-400 uppercase tracking-wider">Antiguos</span>
                    <Icon icon="tabler:school" class="w-4 h-4 text-indigo-600 group-hover:scale-110 transition-transform" />
                </div>
                <div class="text-2xl font-black text-gray-900 dark:text-white">
                    {{ olds }}
                </div>
                <p class="text-[11px] text-gray-400 mt-0.5">Continuantes</p>
            </Link>

            <!-- 4. Alumnos Inactivos / Retirados -->
            <Link
                :href="route('student-list', { status: '0' })"
                class="p-4 rounded-2xl bg-gradient-to-br from-rose-500/10 via-rose-500/5 to-transparent border border-rose-200/60 dark:border-rose-800/60 hover:border-rose-500 transition group hover:shadow-sm"
            >
                <div class="flex items-center justify-between mb-2">
                    <span class="text-xs font-bold text-rose-700 dark:text-rose-400 uppercase tracking-wider">Retirados</span>
                    <Icon icon="tabler:user-x" class="w-4 h-4 text-rose-600 group-hover:scale-110 transition-transform" />
                </div>
                <div class="text-2xl font-black text-gray-900 dark:text-white">
                    {{ inactives }}
                </div>
                <p class="text-[11px] text-gray-400 mt-0.5">Inactivos o traslado</p>
            </Link>

            <!-- 5. Total de Registros -->
            <Link
                :href="route('student-list')"
                class="col-span-2 sm:col-span-1 p-4 rounded-2xl bg-gradient-to-br from-purple-500/10 via-purple-500/5 to-transparent border border-purple-200/60 dark:border-purple-800/60 hover:border-purple-500 transition group hover:shadow-sm"
            >
                <div class="flex items-center justify-between mb-2">
                    <span class="text-xs font-bold text-purple-700 dark:text-purple-400 uppercase tracking-wider">Total</span>
                    <Icon icon="tabler:database" class="w-4 h-4 text-purple-600 group-hover:scale-110 transition-transform" />
                </div>
                <div class="text-2xl font-black text-gray-900 dark:text-white">
                    {{ total }}
                </div>
                <p class="text-[11px] text-gray-400 mt-0.5">Histórico total</p>
            </Link>
        </div>
    </div>
</template>
